!(function (t, e) {
  "function" == typeof define && define.amd
    ? define([], e)
    : "object" == typeof exports
    ? (module.exports = e())
    : (t.Marquee3k = e());
})(this, function () {
  "use strict";
  let MARQUEES = [];
  let isListening = false;

  // Per-instance keyframes with LITERAL pixel values. var() inside @keyframes forces the
  // animation onto the main thread (per-frame style recalc); literals keep it fully
  // compositor-driven — zero main-thread work per frame.
  let m3kSeq = 0;
  let m3kSheet = null;
  function m3kRule(name, fromPx, toPx) {
    if (!m3kSheet) {
      const style = document.createElement("style");
      style.id = "m3k-keyframes";
      document.head.appendChild(style);
      m3kSheet = style;
    }
    m3kSheet.textContent += "@keyframes " + name + "{from{transform:translate3d(" + fromPx +
      "px,0,0)}to{transform:translate3d(" + toPx + "px,0,0)}}\n";
  }

  class e {
    constructor(t, e) {
      if (!t || !t.querySelector(".marquee-scrolling-item")) {
        console.warn("Marquee3k: Element has no .marquee-scrolling-item.");
        return;
      }

      // Skip already-initialized elements to prevent double-init from multiple init() calls
      if (t.classList.contains("is-init")) return;

      this.element = t;
      this.selector = e.selector;
      this.speed = parseFloat(t.dataset.speed) || 1;
      this.pausable = t.dataset.pausable === "true" ? 1 : -1;
      this.reverse = t.dataset.reverse === "true";
      this.paused = false;          // user/API pause (hover, static pause())
      this.offscreen = false;       // viewport pause (IntersectionObserver)
      this.parent = t.parentElement;
      this.parentProps = this.parent.getBoundingClientRect();
      this.content = t.children[0];
      this.innerContent = this.content.innerHTML;
      this._setupWrapper();
      this._setupContent();
      this._setupEvents();
      this.wrapper.appendChild(this.content);
      this.element.appendChild(this.wrapper);
      this._startCssAnimation();
      this._observeViewport();
    }

    _setupWrapper() {
      this.wrapper = document.createElement("div");
      this.wrapper.classList.add("marquee3k__wrapper");
      this.wrapper.style.whiteSpace = "nowrap";
      this.wrapper.style.willChange = "transform";
    }

    _setupContent() {
      this.content.classList.add(`${this.selector}__copy`);
      this.content.style.display = "inline-block";
      this.contentWidth = this.content.offsetWidth;
      this.parentProps = this.parent.getBoundingClientRect();

      // Guard: if contentWidth is 0 the division below produces Infinity → infinite clone loop
      if (!this.contentWidth) return;

      this.requiredReps = Math.min(
        this.contentWidth > this.parentProps.width
          ? 2
          : Math.ceil((this.parentProps.width - this.contentWidth) / this.contentWidth) + 1,
        20  // hard cap — prevents runaway cloning if measurements are unreliable
      );

      for (let i = 0; i < this.requiredReps; i++) this._createClone();
      this.element.classList.add("is-init");
    }

    // Seamless loop: animate exactly one content-width, then the identical clone takes over.
    // Old JS moved speed*delta/16 px per frame ⇒ speed × 62.5 px/second — duration matches
    // that exactly so the visual speed is unchanged.
    _startCssAnimation() {
      const w = this.contentWidth;
      if (!w || !this.wrapper) return;
      const pxPerSec = (this.speed || 1) * 62.5;
      const from = this.reverse ? -w : 0;
      const to = this.reverse ? 0 : -w;
      this._animName = "m3k-a" + (++m3kSeq);
      m3kRule(this._animName, from, to);
      this.wrapper.style.animation = this._animName + " " + (w / pxPerSec) + "s linear infinite";
      this._syncPlayState();
    }

    _syncPlayState() {
      if (!this.wrapper) return;
      this.wrapper.style.animationPlayState = this.paused || this.offscreen ? "paused" : "running";
    }

    // Marquees off the viewport burn zero cycles.
    _observeViewport() {
      if (typeof IntersectionObserver === "undefined") return;
      this._io = new IntersectionObserver((entries) => {
        for (const en of entries) {
          this.offscreen = !en.isIntersecting;
          this._syncPlayState();
        }
      });
      this._io.observe(this.element);
    }

    _setupEvents() {
      // Store bound handlers so they can be removed in destroy()
      this._onMouseEnter = () => {
        if (this.pausable) { this.paused = true; this._syncPlayState(); }
      };
      this._onMouseLeave = () => {
        if (this.pausable) { this.paused = false; this._syncPlayState(); }
      };
      this.element.addEventListener("mouseenter", this._onMouseEnter);
      this.element.addEventListener("mouseleave", this._onMouseLeave);
    }

    _createClone() {
      const clone = this.content.cloneNode(true);
      clone.style.display = "inline-block";
      clone.classList.add(`${this.selector}__copy`);
      this.wrapper.appendChild(clone);
    }

    // Clears all clones and rebuilds the correct number — used on resize
    _reset() {
      // Measure BEFORE touching the DOM.
      // If contentWidth is 0 (element unmeasurable mid-resize / DevTools opening),
      // bail out now — clones and this.contentWidth stay intact so animation keeps running.
      const newContentWidth = this.content.offsetWidth;
      const newParentProps = this.parent.getBoundingClientRect();
      if (!newContentWidth || !newParentProps.width) return;

      // Measurements are valid — safe to remove old clones and rebuild
      while (this.wrapper.children.length > 1) {
        this.wrapper.removeChild(this.wrapper.lastChild);
      }

      this.contentWidth = newContentWidth;
      this.parentProps = newParentProps;

      this.requiredReps = Math.min(
        this.contentWidth > this.parentProps.width
          ? 2
          : Math.ceil((this.parentProps.width - this.contentWidth) / this.contentWidth) + 1,
        20  // hard cap — prevents runaway cloning if measurements are unreliable
      );

      for (let i = 0; i < this.requiredReps; i++) this._createClone();
      this._startCssAnimation();
    }

    // Fully tears down this instance so init() can safely run again on this element
    destroy() {
      this.element.removeEventListener("mouseenter", this._onMouseEnter);
      this.element.removeEventListener("mouseleave", this._onMouseLeave);
      if (this._io) { this._io.disconnect(); this._io = null; }

      // Move original content back out of wrapper before removing it
      this.element.insertBefore(this.content, this.wrapper);
      this.wrapper.remove();

      this.content.classList.remove(`${this.selector}__copy`);
      this.element.classList.remove("is-init");
    }

    // Kept for API compatibility — animation is CSS-driven now, nothing to do per frame.
    animate() {}

    _refresh() {
      this.contentWidth = this.content.offsetWidth;
      this._startCssAnimation();
    }

    _setPaused(v) { this.paused = v; this._syncPlayState(); }

    static refresh(i) { MARQUEES[i]._refresh(); }
    static pause(i) { MARQUEES[i]._setPaused(true); }
    static play(i) { MARQUEES[i]._setPaused(false); }
    static toggle(i) { MARQUEES[i]._setPaused(!MARQUEES[i].paused); }
    static refreshAll() { for (let i = 0; i < MARQUEES.length; i++) MARQUEES[i]._refresh(); }
    static pauseAll() { for (let i = 0; i < MARQUEES.length; i++) MARQUEES[i]._setPaused(true); }
    static playAll() { for (let i = 0; i < MARQUEES.length; i++) MARQUEES[i]._setPaused(false); }
    static toggleAll() { for (let i = 0; i < MARQUEES.length; i++) MARQUEES[i]._setPaused(!MARQUEES[i].paused); }

    static destroyAll() {
      for (let i = 0; i < MARQUEES.length; i++) MARQUEES[i].destroy();
      MARQUEES = [];
      isListening = false;
    }

    static init(options = { selector: "marquee3k" }) {
      if (!document.querySelector(`.${options.selector}`)) return;

      const elements = Array.from(document.querySelectorAll(`.${options.selector}`));
      if (!elements.length) return;


      for (let i = 0; i < elements.length; i++) {
        // Skip elements already initialized — prevents duplicate wrappers/clones
        if (elements[i].classList.contains("is-init")) continue;
        const instance = new e(elements[i], options);
        if (instance.content) {
          MARQUEES.push(instance);
        }
      }

      // Guard ensures only one resize listener exists, even if init() is called
      // multiple times from different files. (No animation loop any more — CSS animates.)
      if (!isListening) {
        let resizeTimer;
        window.addEventListener("resize", () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            // _reset() removes all old clones then rebuilds the correct count —
            // replaces the old repopulate() which only ever added, never removed
            for (let i = 0; i < MARQUEES.length; i++) {
              MARQUEES[i]._reset();
            }
          }, 250);
        });

        isListening = true;
      }
    }
  }

  return e;
});

Marquee3k.init();
